import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FileText, Plus, Building2, User, IndianRupee, Calendar, CheckCircle,
  Clock, RefreshCw, ChevronRight, Download, Settings, CreditCard, FileCheck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import Layout from "../components/Layout";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const formatINR = (v) => {
  if (!v) return "₹0";
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const STATUS_STYLES = {
  booked: "bg-blue-100 text-blue-700",
  agreement_pending: "bg-amber-100 text-amber-700",
  agreement_done: "bg-emerald-100 text-emerald-700",
  registered: "bg-purple-100 text-purple-700",
  cancelled: "bg-red-100 text-red-700",
};

const PAYMENT_STATUS_STYLES = {
  pending: "bg-slate-100 text-slate-600",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-red-100 text-red-700",
};

const BookingsPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("bookings");
  
  // Bookings state
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({});
  const [projects, setProjects] = useState([]);
  
  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  
  // Payment dialog
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentMilestoneIdx, setPaymentMilestoneIdx] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paid_date: new Date().toISOString().split("T")[0],
    paid_amount: 0,
    payment_mode: "neft",
    reference_number: "",
    remarks: ""
  });
  
  // Cost sheet view
  const [costSheetOpen, setCostSheetOpen] = useState(false);
  const [costSheetData, setCostSheetData] = useState(null);
  
  // Allotment letter view
  const [allotmentOpen, setAllotmentOpen] = useState(false);
  const [allotmentData, setAllotmentData] = useState(null);
  
  // Templates config
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [costSheetTemplates, setCostSheetTemplates] = useState([]);

  useEffect(() => {
    loadBookings();
    loadStats();
    loadProjects();
  }, []);

  useEffect(() => {
    loadBookings();
  }, [filterStatus]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      const res = await axios.get(`${API}/bookings`, { params });
      setBookings(res.data);
    } catch (e) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/bookings/stats`);
      setStats(res.data);
    } catch (e) {
      console.error("Failed to load stats");
    }
  };

  const loadProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) {
      console.error("Failed to load projects");
    }
  };

  const viewDetail = async (bookingId) => {
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}`);
      setDetailBooking(res.data);
      setDetailTab("overview");
      setDetailOpen(true);
    } catch (e) {
      toast.error("Failed to load booking details");
    }
  };

  const generateCostSheet = async (bookingId) => {
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/cost-sheet`);
      setCostSheetData(res.data);
      setCostSheetOpen(true);
    } catch (e) {
      toast.error("Failed to generate cost sheet");
    }
  };

  const generateAllotment = async (bookingId) => {
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/allotment-letter`);
      setAllotmentData(res.data);
      setAllotmentOpen(true);
    } catch (e) {
      toast.error("Failed to generate allotment letter");
    }
  };

  const recordPayment = async () => {
    if (!detailBooking || paymentMilestoneIdx === null) return;
    try {
      await axios.put(
        `${API}/bookings/${detailBooking.booking_id}/payment/${paymentMilestoneIdx}`,
        paymentForm
      );
      toast.success("Payment recorded");
      setPaymentOpen(false);
      // Refresh booking detail
      viewDetail(detailBooking.booking_id);
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace(/_/g, " ")}`);
      loadBookings();
      if (detailBooking?.booking_id === bookingId) {
        viewDetail(bookingId);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const loadCostSheetTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await axios.get(`${API}/bookings/templates/cost-sheet`);
      setCostSheetTemplates(res.data);
    } catch (e) {
      console.error("Failed to load templates");
    } finally {
      setTemplatesLoading(false);
    }
  };

  const paidMilestones = detailBooking?.payment_schedule?.filter(m => m.status === "paid") || [];
  const pendingMilestones = detailBooking?.payment_schedule?.filter(m => m.status !== "paid") || [];

  // Filter bookings by search term
  const filteredBookings = bookings.filter(b => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (b.buyer_name || "").toLowerCase().includes(s)
      || (b.buyer_phone || "").toLowerCase().includes(s)
      || (b.buyer_email || "").toLowerCase().includes(s)
      || (b.lead_id || "").toLowerCase().includes(s)
      || (b.unit_number || "").toLowerCase().includes(s)
      || (b.project_name || "").toLowerCase().includes(s);
  });

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="bookings-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Bookings</h1>
            <p className="text-sm text-slate-500 mt-1">Manage bookings, cost sheets, allotment letters & payments</p>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Bookings</p>
                <p className="text-lg font-bold">{stats.total_bookings || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Value</p>
                <p className="text-lg font-bold text-emerald-600">{formatINR(stats.total_value)}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Collected</p>
                <p className="text-lg font-bold text-purple-600">{formatINR(stats.total_collected)}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending Agreement</p>
                <p className="text-lg font-bold text-amber-600">{stats.by_status?.agreement_pending || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "templates") loadCostSheetTemplates(); }}>
          <TabsList className="grid w-full grid-cols-2 h-10">
            <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings</TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">
              <Settings className="w-4 h-4 mr-1.5" />Templates
            </TabsTrigger>
          </TabsList>

          {/* ===== BOOKINGS LIST ===== */}
          <TabsContent value="bookings" className="space-y-4 mt-4">
            <div className="flex gap-2 items-center flex-wrap">
              <div className="relative">
                <Input 
                  placeholder="Search by name, phone, email, lead ID..." 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)} 
                  className="w-[280px] pl-3"
                  data-testid="booking-search"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]" data-testid="booking-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Bookings</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="agreement_pending">Agreement Pending</SelectItem>
                  <SelectItem value="agreement_done">Agreement Done</SelectItem>
                  <SelectItem value="registered">Registered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => { loadBookings(); loadStats(); }} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              </div>
            ) : filteredBookings.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center text-slate-400">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>{searchTerm ? "No bookings match your search" : "No bookings found"}</p>
                  <p className="text-xs mt-1">Bookings are created when negotiations are won</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredBookings.map(b => (
                  <Card 
                    key={b.booking_id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => viewDetail(b.booking_id)}
                    data-testid={`booking-card-${b.booking_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900">{b.buyer_name}</span>
                            <Badge className={`text-xs ${STATUS_STYLES[b.status]}`}>
                              {b.status.replace(/_/g, " ")}
                            </Badge>
                            <Badge variant="outline" className="text-xs">{b.configuration}</Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" /> {b.project_name}
                            </span>
                            <span>Unit: {b.unit_number}</span>
                            <span className="font-medium text-slate-700">
                              {formatINR(b.total_amount)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> 
                              {b.booking_date ? new Date(b.booking_date).toLocaleDateString("en-IN") : "N/A"}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-xs"
                            onClick={(e) => { e.stopPropagation(); generateCostSheet(b.booking_id); }}
                            data-testid={`cost-sheet-${b.booking_id}`}
                          >
                            <FileText className="w-3 h-3 mr-1" /> Cost Sheet
                          </Button>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== TEMPLATES ===== */}
          <TabsContent value="templates" className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">Configure cost sheet and allotment letter templates per project.</p>
            
            {templatesLoading ? (
              <div className="text-center py-12 text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              </div>
            ) : costSheetTemplates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-slate-400">
                  <Settings className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No templates configured</p>
                  <p className="text-xs mt-1">Templates are auto-created when you configure them for a project</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {costSheetTemplates.map(t => (
                  <Card key={t.template_id} data-testid={`template-card-${t.template_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{t.project_name || t.project_id}</span>
                            <Badge className={t.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                              {t.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                            <span>GST: {t.gst_percentage}%</span>
                            <span>Stamp Duty: {t.stamp_duty_percentage}%</span>
                            <span>Registration: {t.registration_percentage}%</span>
                            <span>Plans: {t.payment_plans?.join(", ")}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
            <DialogHeader>
              <DialogTitle>Booking Details — {detailBooking?.buyer_name}</DialogTitle>
            </DialogHeader>
            {detailBooking && (
              <Tabs value={detailTab} onValueChange={setDetailTab} className="mt-2">
                <TabsList className="grid w-full grid-cols-3 h-9">
                  <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
                  <TabsTrigger value="payments" className="text-xs">Payments</TabsTrigger>
                  <TabsTrigger value="documents" className="text-xs">Documents</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4 mt-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={`${STATUS_STYLES[detailBooking.status]}`}>
                      {detailBooking.status.replace(/_/g, " ")}
                    </Badge>
                    {detailBooking.status === "booked" && (
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => updateBookingStatus(detailBooking.booking_id, "agreement_pending")}>
                        Move to Agreement Pending
                      </Button>
                    )}
                    {detailBooking.status === "agreement_pending" && (
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => updateBookingStatus(detailBooking.booking_id, "agreement_done")}>
                        Mark Agreement Done
                      </Button>
                    )}
                    {detailBooking.status === "agreement_done" && (
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => updateBookingStatus(detailBooking.booking_id, "registered")}>
                        Mark Registered
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-2 font-medium">Buyer Details</p>
                      <div className="space-y-1">
                        <p><span className="text-slate-500">Name:</span> <span className="font-medium">{detailBooking.buyer_name}</span></p>
                        <p><span className="text-slate-500">Phone:</span> <span className="font-medium">{detailBooking.buyer_phone}</span></p>
                        {detailBooking.buyer_email && <p><span className="text-slate-500">Email:</span> <span className="font-medium">{detailBooking.buyer_email}</span></p>}
                        {detailBooking.co_applicant_name && <p><span className="text-slate-500">Co-Applicant:</span> <span className="font-medium">{detailBooking.co_applicant_name}</span></p>}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-2 font-medium">Unit Details</p>
                      <div className="space-y-1">
                        <p><span className="text-slate-500">Project:</span> <span className="font-medium">{detailBooking.project_name}</span></p>
                        <p><span className="text-slate-500">Unit:</span> <span className="font-medium">{detailBooking.unit_number}</span></p>
                        {detailBooking.tower && <p><span className="text-slate-500">Tower:</span> <span className="font-medium">{detailBooking.tower}</span></p>}
                        {detailBooking.floor && <p><span className="text-slate-500">Floor:</span> <span className="font-medium">{detailBooking.floor}</span></p>}
                        <p><span className="text-slate-500">Config:</span> <span className="font-medium">{detailBooking.configuration}</span></p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 mb-2 font-medium">Pricing Summary</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Base Price:</span>
                        <span className="font-medium">{formatINR(detailBooking.base_price)}</span>
                      </div>
                      {detailBooking.floor_rise > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Floor Rise:</span>
                          <span className="font-medium">{formatINR(detailBooking.floor_rise)}</span>
                        </div>
                      )}
                      {detailBooking.plc > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">PLC:</span>
                          <span className="font-medium">{formatINR(detailBooking.plc)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-600">Gross:</span>
                        <span className="font-medium">{formatINR(detailBooking.gross_amount)}</span>
                      </div>
                      {detailBooking.discount_amount > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>Discount ({detailBooking.discount_percentage}%):</span>
                          <span className="font-medium">-{formatINR(detailBooking.discount_amount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-1">
                        <span className="text-slate-600">Net:</span>
                        <span className="font-medium">{formatINR(detailBooking.net_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">GST:</span>
                        <span className="font-medium">{formatINR(detailBooking.gst_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Stamp Duty:</span>
                        <span className="font-medium">{formatINR(detailBooking.stamp_duty)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Registration:</span>
                        <span className="font-medium">{formatINR(detailBooking.registration)}</span>
                      </div>
                      <div className="flex justify-between col-span-2 border-t pt-2 text-base">
                        <span className="font-bold text-slate-800">Total:</span>
                        <span className="font-bold text-emerald-600">{formatINR(detailBooking.total_amount)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 text-sm">
                    <span className="text-slate-500">Payment Plan:</span>
                    <Badge variant="outline">{detailBooking.payment_plan}</Badge>
                  </div>
                </TabsContent>

                {/* Payments Tab */}
                <TabsContent value="payments" className="space-y-4 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Payment Schedule</p>
                      <p className="text-xs text-slate-500">{paidMilestones.length} of {detailBooking.payment_schedule?.length || 0} milestones paid</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Total Collected</p>
                      <p className="text-lg font-bold text-emerald-600">
                        {formatINR(paidMilestones.reduce((s, m) => s + (m.paid_amount || 0), 0))}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {detailBooking.payment_schedule?.map((m, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${m.status === "paid" ? "bg-emerald-50 border-emerald-200" : "bg-white"}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${m.status === "paid" ? "bg-emerald-100" : "bg-slate-100"}`}>
                            {m.status === "paid" ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <span className="text-xs font-bold text-slate-500">{idx + 1}</span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{m.milestone}</p>
                            <div className="flex gap-2 text-xs text-slate-500">
                              <span>{m.percentage}%</span>
                              <span>•</span>
                              <span>{formatINR(m.amount)}</span>
                              {m.status === "paid" && m.paid_date && (
                                <>
                                  <span>•</span>
                                  <span className="text-emerald-600">Paid {m.paid_date}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs ${PAYMENT_STATUS_STYLES[m.status]}`}>
                            {m.status}
                          </Badge>
                          {m.status !== "paid" && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-xs"
                              onClick={() => {
                                setPaymentMilestoneIdx(idx);
                                setPaymentForm({
                                  paid_date: new Date().toISOString().split("T")[0],
                                  paid_amount: m.amount,
                                  payment_mode: "neft",
                                  reference_number: "",
                                  remarks: ""
                                });
                                setPaymentOpen(true);
                              }}
                              data-testid={`record-payment-${idx}`}
                            >
                              Record Payment
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" className="space-y-4 mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => generateCostSheet(detailBooking.booking_id)}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-700" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Cost Sheet</p>
                          <p className="text-xs text-slate-500">View / Download</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => generateAllotment(detailBooking.booking_id)}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <FileCheck className="w-5 h-5 text-purple-700" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Allotment Letter</p>
                          <p className="text-xs text-slate-500">View / Download</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
          <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            {detailBooking && paymentMilestoneIdx !== null && (
              <div className="space-y-4 py-2">
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="font-medium">{detailBooking.payment_schedule[paymentMilestoneIdx]?.milestone}</p>
                  <p className="text-slate-500">Expected: {formatINR(detailBooking.payment_schedule[paymentMilestoneIdx]?.amount)}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Payment Date</label>
                    <Input 
                      type="date" 
                      value={paymentForm.paid_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paid_date: e.target.value })}
                      data-testid="payment-date"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Amount Received</label>
                    <Input 
                      type="number" 
                      value={paymentForm.paid_amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paid_amount: parseFloat(e.target.value) || 0 })}
                      data-testid="payment-amount"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Payment Mode</label>
                  <Select value={paymentForm.payment_mode} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_mode: v })}>
                    <SelectTrigger data-testid="payment-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neft">NEFT/RTGS</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="dd">Demand Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Reference Number</label>
                  <Input 
                    value={paymentForm.reference_number}
                    onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                    placeholder="Transaction ID / Cheque No."
                    data-testid="payment-reference"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Remarks</label>
                  <Textarea 
                    value={paymentForm.remarks}
                    onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
                    rows={2}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentOpen(false)}>Cancel</Button>
              <Button onClick={recordPayment} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-payment-btn">
                Record Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cost Sheet Dialog */}
        <Dialog open={costSheetOpen} onOpenChange={setCostSheetOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cost-sheet-dialog">
            <DialogHeader>
              <DialogTitle>Cost Sheet</DialogTitle>
            </DialogHeader>
            {costSheetData && (
              <div className="space-y-4 py-2 text-sm" id="cost-sheet-content">
                <div className="text-center border-b pb-3">
                  <h2 className="text-lg font-bold text-slate-900">COST SHEET</h2>
                  <p className="text-slate-500">{costSheetData.project_name}</p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Buyer:</span> <span className="font-medium">{costSheetData.buyer_name}</span></div>
                  <div><span className="text-slate-500">Unit:</span> <span className="font-medium">{costSheetData.unit_number}</span></div>
                  {costSheetData.tower && <div><span className="text-slate-500">Tower:</span> <span className="font-medium">{costSheetData.tower}</span></div>}
                  {costSheetData.floor && <div><span className="text-slate-500">Floor:</span> <span className="font-medium">{costSheetData.floor}</span></div>}
                  <div><span className="text-slate-500">Config:</span> <span className="font-medium">{costSheetData.configuration}</span></div>
                  {costSheetData.super_built_up_area && <div><span className="text-slate-500">SBA:</span> <span className="font-medium">{costSheetData.super_built_up_area} sq.ft.</span></div>}
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">Particulars</th>
                        <th className="text-right py-2 px-3 text-xs font-medium text-slate-500">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costSheetData.line_items?.map((item, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3">{item.label}</td>
                          <td className="py-2 px-3 text-right font-medium">{formatINR(item.amount)}</td>
                        </tr>
                      ))}
                      <tr className="border-t bg-slate-50">
                        <td className="py-2 px-3 font-medium">Gross Amount</td>
                        <td className="py-2 px-3 text-right font-bold">{formatINR(costSheetData.gross_amount)}</td>
                      </tr>
                      {costSheetData.discount > 0 && (
                        <tr className="border-t text-red-600">
                          <td className="py-2 px-3">Less: Discount ({costSheetData.discount_percentage}%)</td>
                          <td className="py-2 px-3 text-right font-medium">-{formatINR(costSheetData.discount)}</td>
                        </tr>
                      )}
                      <tr className="border-t">
                        <td className="py-2 px-3 font-medium">Net Amount</td>
                        <td className="py-2 px-3 text-right font-bold">{formatINR(costSheetData.net_amount)}</td>
                      </tr>
                      <tr className="border-t">
                        <td className="py-2 px-3">GST</td>
                        <td className="py-2 px-3 text-right">{formatINR(costSheetData.gst)}</td>
                      </tr>
                      <tr className="border-t">
                        <td className="py-2 px-3">Stamp Duty</td>
                        <td className="py-2 px-3 text-right">{formatINR(costSheetData.stamp_duty)}</td>
                      </tr>
                      <tr className="border-t">
                        <td className="py-2 px-3">Registration</td>
                        <td className="py-2 px-3 text-right">{formatINR(costSheetData.registration)}</td>
                      </tr>
                      <tr className="border-t bg-emerald-50">
                        <td className="py-2 px-3 font-bold text-emerald-800">Total Consideration</td>
                        <td className="py-2 px-3 text-right font-bold text-emerald-700 text-lg">{formatINR(costSheetData.total_amount)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {costSheetData.payment_schedule && costSheetData.payment_schedule.length > 0 && (
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Payment Schedule ({costSheetData.payment_plan})</p>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left py-2 px-3">Milestone</th>
                            <th className="text-center py-2 px-3">%</th>
                            <th className="text-right py-2 px-3">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costSheetData.payment_schedule.map((m, i) => (
                            <tr key={i} className="border-t">
                              <td className="py-1.5 px-3">{m.milestone}</td>
                              <td className="py-1.5 px-3 text-center">{m.percentage}%</td>
                              <td className="py-1.5 px-3 text-right">{formatINR(m.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <p className="text-xs text-slate-400 text-center mt-4">
                  Generated on {new Date(costSheetData.generated_at).toLocaleString("en-IN")}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCostSheetOpen(false)}>Close</Button>
              <Button onClick={() => window.print()}>
                <Download className="w-4 h-4 mr-1" /> Print / Save PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Allotment Letter Dialog */}
        <Dialog open={allotmentOpen} onOpenChange={setAllotmentOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="allotment-dialog">
            <DialogHeader>
              <DialogTitle>Allotment Letter</DialogTitle>
            </DialogHeader>
            {allotmentData && (
              <div className="space-y-4 py-2 text-sm" id="allotment-content">
                {allotmentData.company_name && (
                  <div className="text-center border-b pb-3">
                    <h2 className="text-lg font-bold text-slate-900">{allotmentData.company_name}</h2>
                    {allotmentData.company_address && <p className="text-xs text-slate-500">{allotmentData.company_address}</p>}
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-base font-bold text-slate-800 underline">{allotmentData.header_text}</h3>
                </div>

                <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                  {allotmentData.body}
                </div>

                {allotmentData.terms_and_conditions && (
                  <div className="border-t pt-3">
                    <p className="text-xs font-medium text-slate-500 mb-1">Terms & Conditions:</p>
                    <p className="text-xs text-slate-600 whitespace-pre-wrap">{allotmentData.terms_and_conditions}</p>
                  </div>
                )}

                {allotmentData.signatory_name && (
                  <div className="text-right mt-6">
                    <p className="font-medium text-slate-800">{allotmentData.signatory_name}</p>
                    {allotmentData.signatory_designation && <p className="text-xs text-slate-500">{allotmentData.signatory_designation}</p>}
                  </div>
                )}

                <p className="text-xs text-slate-400 text-center mt-4">
                  Generated on {new Date(allotmentData.generated_at).toLocaleString("en-IN")}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAllotmentOpen(false)}>Close</Button>
              <Button onClick={() => window.print()}>
                <Download className="w-4 h-4 mr-1" /> Print / Save PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BookingsPage;
